import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';
import { useConfirm } from '../context/ConfirmContext';
import './Companies.css';

const Companies = () => {
  const { t } = useTranslation(['companies', 'superAdmin', 'common', 'users', 'company']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    pricingPlan: 'basic'
  });
  const [error, setError] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: ''
  });
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError(t('companies:fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingCompany) {
        await axios.put(`/api/companies/${editingCompany.id}`, formData);
      } else {
        await axios.post('/api/companies', {
          ...formData,
          pricingPlan: formData.pricingPlan
        });
      }
      setShowModal(false);
      setEditingCompany(null);
      setFormData({ name: '', address: '', phone: '', email: '', pricingPlan: 'basic' });
      fetchCompanies();
    } catch (error) {
      setError(
        formatError(error) ||
          (editingCompany ? t('companies:updateFailed') : t('companies:createFailed'))
      );
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      pricingPlan: company.pricing_plan || 'basic'
    });
    setInviteForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'employee'
    });
    setInviteError('');
    setInviteSuccess('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCompany(null);
    setFormData({ name: '', address: '', phone: '', email: '', pricingPlan: 'basic' });
    setInviteForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'employee'
    });
    setInviteError('');
    setInviteSuccess('');
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t('companies:deleteConfirm'), { danger: true }))) {
      return;
    }

    try {
      await axios.delete(`/api/companies/${id}`);
      fetchCompanies();
    } catch (error) {
      setError(formatError(error) || t('companies:deleteFailed'));
    }
  };

  const handleInviteChange = (e) => {
    setInviteForm({
      ...inviteForm,
      [e.target.name]: e.target.value
    });
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!editingCompany) {
      setInviteError(t('companies:inviteSelectCompany'));
      return;
    }

    try {
      const response = await axios.post(`/api/companies/${editingCompany.id}/invite`, inviteForm);
      const data = response.data.data;

      if (data.message) {
        setInviteSuccess(t('company:inviteSent', { email: data.email || inviteForm.email }));
      }

      setInviteForm({
        email: ''
      });
      fetchCompanies();
    } catch (error) {
      setInviteError(formatError(error) || t('company:failedInvite'));
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="companies">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('companies:title')}</h1>
          {user?.role === 'super_admin' && (
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              {t('companies:createCompany')}
            </button>
          )}
        </div>

        {error && <div className="error">{error}</div>}

        <div className="card">
          {companies.length > 0 ? (
            <>
            <table className="table admin-mobile-table">
              <thead>
                <tr>
                  <th>{t('common:name')}</th>
                  <th>{t('common:email')}</th>
                  <th>{t('common:phone')}</th>
                  <th>{t('companies:tableAddress')}</th>
                  <th>{t('companies:tableUserCount')}</th>
                  <th>{t('common:actions')}</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td>{company.name}</td>
                    <td>{company.email || t('common:emptyCell')}</td>
                    <td>{company.phone || t('common:emptyCell')}</td>
                    <td>{company.address || t('common:emptyCell')}</td>
                    <td>
                      {company.user_count || 0}
                      {company.max_users != null ? ` / ${company.max_users}` : ''}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(company)}
                        >
                          {t('common:edit')}
                        </button>
                        {user?.role === 'super_admin' && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(company.id)}
                          >
                            {t('common:delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="admin-cards-mobile companies-cards-mobile">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className="sim-task-card admin-mobile-card companies-mobile-card"
                  onClick={() => handleEdit(company)}
                >
                  <div className="admin-mobile-card-title">{company.name}</div>
                  {company.email && (
                    <p className="admin-mobile-card-meta">{company.email}</p>
                  )}
                  <p className="admin-mobile-card-meta">
                    {company.user_count || 0}
                    {company.max_users != null ? ` / ${company.max_users}` : ''}{' '}
                    {t('companies:usersLabel')}
                  </p>
                  {user?.role === 'super_admin' && (
                    <div className="companies-mobile-card-actions">
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(company.id);
                        }}
                      >
                        {t('common:delete')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            </>
          ) : (
            <EmptyState
              icon="business"
              title={t('companies:emptyTitle')}
              description={t('companies:emptyDescription')}
              actionLabel={user?.role === 'super_admin' ? t('companies:createCompany') : undefined}
              onAction={user?.role === 'super_admin' ? () => setShowModal(true) : undefined}
            />
          )}
        </div>

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingCompany ? t('companies:editCompany') : t('companies:createCompany')}
          className="companies-modal-panel"
        >
          <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('company:nameRequired')}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('common:email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common:phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t('company:addressLabel')}</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </div>
                {!editingCompany && (
                  <div className="form-group">
                    <label>{t('companies:formPricingPlan')}</label>
                    <select
                      name="pricingPlan"
                      value={formData.pricingPlan}
                      onChange={handleChange}
                      required
                    >
                      <option value="basic">{t('companies:pricingPlanBasicOption')}</option>
                      <option value="professional">{t('companies:pricingPlanProfessionalOption')}</option>
                      <option value="enterprise">{t('companies:pricingPlanEnterpriseOption')}</option>
                    </select>
                  </div>
                )}
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingCompany ? t('common:update') : t('common:create')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
              {editingCompany && user?.role === 'super_admin' && (() => {
                const atCap =
                  editingCompany.max_users != null &&
                  Number(editingCompany.user_count || 0) >= editingCompany.max_users;
                return (
                  <div className="invite-section">
                    <h3>{t('company:inviteTitle')}</h3>
                    <p className="invite-description">
                      {t('companies:inviteSectionDescription')}
                    </p>
                    {editingCompany.max_users != null && (
                      <p className="invite-cap-label">
                        {t('companies:inviteUserCapLabel', {
                          count: editingCompany.user_count || 0,
                          max: editingCompany.max_users
                        })}
                      </p>
                    )}
                    {atCap && (
                      <div className="error">
                        {t('companies:inviteUserCapReached')}
                      </div>
                    )}
                    {inviteError && <div className="error">{inviteError}</div>}
                    {inviteSuccess && (
                      <div className="success">
                        {inviteSuccess}
                      </div>
                    )}
                    <form onSubmit={handleInviteSubmit}>
                      <div className="form-group">
                        <label>{t('company:inviteEmailLabel')}</label>
                        <input
                          type="email"
                          name="email"
                          value={inviteForm.email}
                          onChange={handleInviteChange}
                          required
                          disabled={atCap}
                          placeholder={t('company:inviteEmailPlaceholder')}
                        />
                      </div>
                      <div className="modal-actions">
                        <button type="submit" className="btn btn-primary" disabled={atCap}>
                          {t('company:sendInvitation')}
                        </button>
                      </div>
                    </form>
                  </div>
                );
              })()}
        </Modal>
      </div>
    </div>
  );
};

export default Companies;
